<mat-card-actions class="fh-toolbar">
  <!-- Left: metrics -->
  <mat-chip-listbox aria-label="Metrics selection" multiple>
    @for (metric of metrics; track metric) {
      <mat-chip-option
        (click)="toggleMetric(metric)"
        [selected]="isSelectedMetric(metric)">
        {{ metric.displayName }}
      </mat-chip-option>
    }
  </mat-chip-listbox>

  <div class="spacer"></div>

  <!-- Right cluster: data source + period types + last modified + buttons -->
  <div class="right-group">
    @if (!isDataset && !isEditable) {
      <mat-chip-listbox aria-label="Data source selection" multiple class="data-source-list">
        <mat-chip-option
          (click)="toggleGpProjected()"
          [selected]="showGpProjected">
          GP Projected
        </mat-chip-option>
      </mat-chip-listbox>

      <mat-chip-listbox aria-label="Period type selection" multiple class="period-type-list">
        @for (type of periodTypesFilter; track type) {
          <mat-chip-option
            (click)="toggleType(type)"
            [selected]="isSelectedType(type)">
            {{ type.displayName }}
          </mat-chip-option>
        }
      </mat-chip-listbox>
    }

    <div class="last-modified" data-test="modification-date">
      Last modified: {{ getLastModified() | timestamp }}
    </div>

    @if (!isEditable) {
      <button (click)="edit()"
        [disabled]="isEditDisabled!"
        color="primary"
        data-test="edit-button"
        mat-icon-button>
        <mat-icon>edit</mat-icon>
      </button>
    }

    @if (isEditable) {
      <button (click)="save()"
        color="primary"
        data-test="save-button"
        mat-icon-button>
        <mat-icon>save</mat-icon>
      </button>
    }
    @if (isEditable) {
      <button (click)="cancel()"
        color="primary"
        data-test="cancel-button"
        mat-icon-button>
        <mat-icon>cancel</mat-icon>
      </button>
    }
  </div>
</mat-card-actions>


<mat-card-content class="detail-card-content">
  <section class="table-container" tabindex="0">
    @if (selectedAssetFinancialPerformanceData.length === 0 && !isEditable) {
      <div style="color: grey">
        No financial performance records match the selected period types. Select different period types or add a new record.
      </div>
    }

    @if (selectedAssetFinancialPerformanceData.length > 0) {
      <div>
        <table mat-table [dataSource]="fieldNames">
          <ng-container matColumnDef="rowName" sticky>
            <th mat-header-cell *matHeaderCellDef>
              FYE {{ this.fiscalYearEndMonth | codeFormat: codeTableFiscalYearEnd | async }}
            </th>
            <td mat-cell *matCellDef="let fieldName">
              {{ fieldDisplayNames[fieldName] || fieldName }}
              @if (fieldName === 'netDebt') {
                <mat-icon class="info-icon"
                          matTooltip="If Net Debt is not entered for a period, it is assumed to be 0 for Common Equity, Enterprise Value, Exit Proceeds and Bid Price calculations.">
                  info_outlined
                </mat-icon>
              }
            </td>
          </ng-container>
          @for (record of selectedAssetFinancialPerformanceData; track record; let i = $index) {
            <ng-container matColumnDef="{{columnKey(record)}}">
              <th mat-header-cell *matHeaderCellDef [class.gp-projected-header]="record.gpProjected">
                <div style="display: flex; align-items: center;">
                  @if (!isEditable) {
                    <div>
                      {{ record.period | traceableFormat }}@if (record.gpProjected) { <span class="gp-projected-badge">(P)</span>}
                    </div>
                  }
                  @if (isEditable) {
                    <div (click)="openPeriodSelectorDialog(actionType.Edit, i)" style="cursor: pointer; color: #4949CB;">
                      {{ getPeriodFromFormGroup(i) | traceableFormat }}@if (record.gpProjected) { <span class="gp-projected-badge">(P)</span>}
                    </div>
                  }
                  @if ((validatePeriodDate(record.period.keyDate, record.period.type) | async) === false) {
                    <div>&nbsp;
                      <mat-icon [matTooltip]="'The period end date ' + formatDate(record.period.keyDate) + ' is inconsistent with the current fiscal year end month.'">
                        error_outlined
                      </mat-icon>
                    </div>
                  }
                </div>
              </th>
              <td mat-cell *matCellDef="let fieldName" [class.gp-projected-cell]="record.gpProjected">
                @if (isFieldEditable(fieldName)) {
                  @for (baselineRecord of baselineAssetFinancialPerformanceData; track baselineRecord) {
                    @if (record.period.type === baselineRecord.period.type && record.period.keyDate === baselineRecord.period.keyDate && record.gpProjected === baselineRecord.gpProjected && isDataset) {
                      <mat-chip-option class="chip"
                                                     (click)="overwriteValue(
                                                    isEditable,
                                                    getFormControl(record.period.keyDate, record.period.type, fieldName, record.gpProjected),
                                                    getFieldValue(fieldName,true,baselineRecord.period.keyDate,baselineRecord.period.type,baselineRecord.gpProjected))"
                                                     [ngClass]="getChipColor(
                                                    getFormControl(record.period.keyDate, record.period.type, fieldName, record.gpProjected),
                                                    getFieldValue(fieldName,true,baselineRecord.period.keyDate,baselineRecord.period.type,baselineRecord.gpProjected),
                                                    getFieldValue(fieldName,false,record.period.keyDate,record.period.type,record.gpProjected))">
                        {{ getFieldValue(fieldName, true, baselineRecord.period.keyDate, baselineRecord.period.type, baselineRecord.gpProjected) | traceableFormat }}
                      </mat-chip-option>
                    }
                  }
                }
                @if (!isEditable) {
                  <span>{{ getFieldValue(fieldName, false, record.period.keyDate, record.period.type, record.gpProjected) | traceableFormat }}</span>
                }
                @if (isEditable) {
                  @if (isFieldEditable(fieldName)) {
                    <input class="input-field input-number-narrow" type="text"
                      [formControl]="getFormControl(record.period.keyDate, record.period.type, fieldName, record.gpProjected)"
                      (change)="calc(record.period.keyDate, record.period.type, fieldName, record.gpProjected)">
                  }
                  @if (!isFieldEditable(fieldName)) {
                    <span>{{ getFieldValue(fieldName, false, record.period.keyDate, record.period.type, record.gpProjected) | traceableFormat }}</span>
                  }
                }
              </td>
            </ng-container>
          }
          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let fieldName; columns: displayedColumns;" [class]="styleRowImportant(fieldName)"></tr>
        </table>
        <div class="info-left">(!) calculation of growth metrics from one period to another.</div>
      </div>
    }

    @if (isEditable) {
      <div class="add-record-button">
        <button (click)="openPeriodSelectorDialog(actionType.Add)" mat-stroked-button color="primary">Add New Record</button>
      </div>
    }
  </section>
</mat-card-content>

